import { useState, useEffect, useCallback, Fragment } from 'react';
import { useNavigate } from 'react-router-dom';
import { getJobs } from '../utils/api';
import ExportButton from '../components/ExportButton';
import { formatCurrency, formatDateShort, getStatusBadgeClass, getServiceFromJob } from '../utils/format';


function displayRep(job) {
  if (!job.sales_rep) return '—';
  if (!job.sales_rep_2) return job.sales_rep;
  const s1 = job.sales_rep_split || '50';
  const s2 = job.sales_rep_2_split || '50';
  return `${job.sales_rep} (${s1}%) / ${job.sales_rep_2} (${s2}%)`;
}

const STATUS_FILTERS = [
  { label: 'All', value: 'all' },
  { label: 'Active', value: 'Active' },
  { label: 'Cancelled', value: 'Cancelled' },
];

const PRODUCT_ORDER = ['roof','coating','paint','windows','gutters','insulation','patio','fencing'];

const PRODUCT_COLORS = {
  roof:       '#b4c5e0',
  coating:    '#ADD8E6',
  paint:      '#C9E4F2',  // slightly lighter shade of coating blue — similar services
  windows:    '#FF5050',
  insulation: '#CC66FF',
  gutters:    '#FFE0B2',
  patio:      '#FFF9C4',
  fencing:    '#F8BBD0',
};

function getProductColor(service) {
  if (!service) return '#CC66FF';
  const s = service.toLowerCase();
  for (const [key, color] of Object.entries(PRODUCT_COLORS)) {
    if (s.includes(key)) return color;
  }
  return '#CC66FF'; // anything else = purple
}

function getProductOrder(service) {
  if (!service) return 99;
  const s = service.toLowerCase();
  const idx = PRODUCT_ORDER.findIndex(p => s.includes(p));
  return idx === -1 ? 98 : idx;
}

function sortByProduct(jobs) {
  return [...jobs].sort((a, b) => {
    return getProductOrder(getServiceFromJob(a)) - getProductOrder(getServiceFromJob(b));
  });
}

const PRODUCT_LABELS = {
  roof: 'Roofing', coating: 'Coating', paint: 'Paint', windows: 'Windows', gutters: 'Gutters',
  insulation: 'Insulation', patio: 'Patio', fencing: 'Fencing',
};

// Group jobs by product (in PRODUCT_ORDER), newest contract date first within each group.
function groupByProduct(jobs) {
  const byKey = {};
  for (const job of jobs) {
    const order = getProductOrder(getServiceFromJob(job));
    const key = PRODUCT_ORDER[order] !== undefined ? PRODUCT_ORDER[order] : 'other';
    (byKey[key] = byKey[key] || []).push(job);
  }
  const orderedKeys = [...PRODUCT_ORDER, 'other'].filter(k => byKey[k] && byKey[k].length);
  return orderedKeys.map(key => {
    const rows = byKey[key].sort((a, b) => {
      const da = a.contract_date ? new Date(a.contract_date).getTime() : 0;
      const db = b.contract_date ? new Date(b.contract_date).getTime() : 0;
      return db - da; // newest first
    });
    return {
      key,
      label: PRODUCT_LABELS[key] || (getServiceFromJob(rows[0]) !== '—' ? getServiceFromJob(rows[0]) : 'Other'),
      color: getProductColor(key === 'other' ? null : key),
      rows,
    };
  });
}

const TH = ({ children, right }) => (
  <th style={{
    padding:'4px 4px', textAlign: right ? 'right' : 'left',
    background:'#1f314e', color:'#fff', fontSize:'7px',
    fontWeight:700, whiteSpace:'nowrap', borderRight:'1px solid #2c466f'
  }}>{children}</th>
);

const TD = ({ children, right, bold, small, bg }) => (
  <td style={{
    padding:'3px 4px', textAlign: right ? 'right' : 'left',
    fontSize: small ? '6.5px' : '7.5px',
    fontWeight: bold ? 700 : 400,
    borderBottom:'1px solid rgba(0,0,0,0.08)',
    borderRight:'1px solid rgba(0,0,0,0.06)',
    background: bg || 'transparent',
    whiteSpace:'nowrap',
  }}>{children || ''}</td>
);

export default function JobTracker() {
  const [jobs, setJobs] = useState([]);
  const [total, setTotal] = useState(0);
  const [status, setStatus] = useState('all');
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const load = useCallback(() => {
    setLoading(true);
    const params = { limit: 200, status: 'tracker' };
    if (search) params.search = search;
    getJobs(params)
      .then(d => { setJobs(d.jobs); setTotal(d.total); })
      .finally(() => setLoading(false));
  }, [search]);

  useEffect(() => { load(); }, [load]);

  const printJobs = sortByProduct(jobs);

  return (
    <>
      <div className="page-header">
        <h2>Job Tracker</h2>
        <div style={{display:'flex',gap:10,alignItems:'center'}}>
          <span style={{ fontSize: '0.84rem', color: 'var(--gray-500)' }}>{total} records</span>
          <button className="btn btn-outline btn-sm" onClick={() => window.print()}>
            <svg viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M5 4v3H4a2 2 0 00-2 2v3a2 2 0 002 2h1v2a1 1 0 001 1h8a1 1 0 001-1v-2h1a2 2 0 002-2V9a2 2 0 00-2-2h-1V4a1 1 0 00-1-1H6a1 1 0 00-1 1zm2 0h6v3H7V4zm-1 9h8v4H6v-4zm-2-4a1 1 0 100 2 1 1 0 000-2z" clipRule="evenodd"/></svg>
            Print
          </button>
          <ExportButton sheet='tracker' />
        </div>
      </div>

      <div className="page-body">
        <div className="filter-bar">

          <div className="search-input-wrap">
            <svg viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z" clipRule="evenodd" />
            </svg>
            <input className="search-input" placeholder="Search PO, name, address, phone, city…" value={search} onChange={e => setSearch(e.target.value)} />
          </div>
        </div>

        <div className="screen-only" style={{ display: 'flex', flexWrap: 'wrap', gap: 12, marginBottom: 14, fontSize: '0.76rem', color: 'var(--gray-600, #555)' }}>
          {[['roof','Roofing'],['coating','Coating'],['paint','Paint'],['windows','Windows'],['gutters','Gutters'],['insulation','Insulation'],['patio','Patio'],['fencing','Fencing']].map(([key, label]) => (
            <span key={key} style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
              <span style={{ width: 12, height: 12, borderRadius: 3, background: getProductColor(key), display: 'inline-block', border: '1px solid rgba(0,0,0,0.12)' }} />
              {label}
            </span>
          ))}
        </div>

        {loading ? (
          <div className="loader"><div className="spinner" /></div>
        ) : jobs.length === 0 ? (
          <div className="empty-state">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
              <path strokeLinecap="round" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            <p>No jobs match your filters</p>
          </div>
        ) : (
          <>
            {/* ── SCREEN VIEW ── */}
            <div className="screen-only table-wrap">
              <table>
                <thead>
                  <tr>
                    <th>PO #</th><th>Customer</th><th>City</th><th>Service</th>
                    <th>Sales Rep</th><th>Contract</th><th>Sales Status</th>
                    <th>Prod Status</th><th>COD</th><th>Deposit</th>
                    <th>Dep. Status</th><th>Rem Date</th><th>Start</th><th>End</th>
                  </tr>
                </thead>
                <tbody>
                  {(() => {
                    const groups = groupByProduct(jobs);
                    let grandTotal = 0;
                    const groupVal = (g) => g.rows.reduce((s, j) => s + (parseFloat(j.change_order_amt || j.contract_amt) || 0), 0);
                    const rendered = groups.map(group => {
                      const subtotal = groupVal(group);
                      grandTotal += subtotal;
                      return (
                      <Fragment key={group.key}>
                        <tr>
                          <td colSpan={14} style={{ background: group.color, fontWeight: 700, color: '#1a1a1a', padding: '6px 10px', borderTop: '2px solid #1f314e', fontSize: '0.85rem' }}>
                            {group.label} <span style={{ fontWeight: 400, color: '#333' }}>({group.rows.length})</span>
                          </td>
                        </tr>
                        {group.rows.map(job => {
                          const svc = getServiceFromJob(job);
                          return (
                          <tr key={job.id} onClick={() => navigate(`/update?po=${job.po_number}`)}
                            style={{ background: group.color, cursor: 'pointer' }}>
                            <td className="td-po">{job.po_number}</td>
                            <td className="td-name">{job.customer_name}</td>
                            <td>{job.city || '—'}</td>
                            <td>{svc || '—'}</td>
                            <td>{displayRep(job)}</td>
                            <td className="td-amt">{formatCurrency(job.change_order_amt || job.contract_amt)}</td>
                            <td><span className={getStatusBadgeClass(job.sales_status)}>{job.sales_status || '—'}</span></td>
                            <td><span className={getStatusBadgeClass(job.prod_status)}>{job.prod_status || '—'}</span></td>
                            <td style={{ fontSize: '0.8rem' }}>{job.primary_lender || '—'}</td>
                            <td className="td-amt">{job.deposit_amt ? formatCurrency(job.deposit_amt) : '—'}</td>
                            <td><span className={getStatusBadgeClass(job.deposit_status)}>{job.deposit_status || '—'}</span></td>
                            <td>{formatDateShort(job.rem_date)}</td>
                            <td>{formatDateShort(job.start_date)}</td>
                            <td>{formatDateShort(job.end_date)}</td>
                          </tr>
                          );
                        })}
                        <tr>
                          <td colSpan={5} style={{ background: '#111e32', color: '#f0c842', fontWeight: 700, textAlign: 'right', padding: '6px 10px', fontSize: '0.82rem' }}>
                            {group.label} Total
                          </td>
                          <td className="td-amt" style={{ background: '#111e32', color: '#f0c842', fontWeight: 800, fontSize: '0.9rem' }}>{formatCurrency(subtotal)}</td>
                          <td colSpan={8} style={{ background: '#111e32' }}></td>
                        </tr>
                      </Fragment>
                      );
                    });
                    return (
                      <>
                        {rendered}
                        <tr>
                          <td colSpan={5} style={{ background: '#0d1f3b', color: '#f0c842', fontWeight: 800, textAlign: 'right', padding: '9px 10px', fontSize: '0.95rem', borderTop: '3px solid #f0c842' }}>
                            GRAND TOTAL — All Jobs
                          </td>
                          <td className="td-amt" style={{ background: '#0d1f3b', color: '#f0c842', fontWeight: 900, fontSize: '1rem', borderTop: '3px solid #f0c842' }}>{formatCurrency(grandTotal)}</td>
                          <td colSpan={8} style={{ background: '#0d1f3b', borderTop: '3px solid #f0c842' }}></td>
                        </tr>
                      </>
                    );
                  })()}
                </tbody>
              </table>
            </div>

            {/* ── PRINT VIEW — all 26 columns, grouped by product ── */}
            <div className="print-only">
              <div style={{marginBottom:'6px',borderBottom:'2px solid #1f314e',paddingBottom:'4px'}}>
                <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-end'}}>
                  <h2 style={{fontFamily:'Georgia,serif',color:'#1f314e',fontSize:'14px',margin:0}}>Amanda's Home Improvement — Job Tracker</h2>
                  <p style={{fontSize:'8px',color:'#555',margin:0}}>Printed: {new Date().toLocaleDateString()} &nbsp;|&nbsp; {total} records</p>
                </div>
              </div>
              <table style={{width:'100%',borderCollapse:'collapse',tableLayout:'fixed'}}>
                <colgroup>
                  <col style={{width:'6%'}}/>  {/* Job# */}
                  <col style={{width:'5%'}}/>  {/* Date */}
                  <col style={{width:'9%'}}/>  {/* Name */}
                  <col style={{width:'4%'}}/>  {/* Phone */}
                  <col style={{width:'9%'}}/>  {/* Address */}
                  <col style={{width:'5%'}}/>  {/* City */}
                  <col style={{width:'4%'}}/>  {/* Product */}
                  <col style={{width:'5%'}}/>  {/* Sales Rep */}
                  <col style={{width:'5%'}}/>  {/* Job Status */}
                  <col style={{width:'4%'}}/>  {/* Rem Date */}
                  <col style={{width:'7%'}}/>  {/* COD */}
                  <col style={{width:'7%'}}/>  {/* Sub */}
                  <col style={{width:'4%'}}/>  {/* Amt Sold */}
                  <col style={{width:'4%'}}/>  {/* Deposit */}
                  <col style={{width:'3%'}}/>  {/* Dep Sta */}
                  <col style={{width:'4%'}}/>  {/* Rem Coll */}
                  <col style={{width:'3%'}}/>  {/* Rem Sta */}
                  <col style={{width:'4%'}}/>  {/* Sta Coll */}
                  <col style={{width:'3%'}}/>  {/* Sta Sta */}
                  <col style={{width:'4%'}}/>  {/* Cmp Coll */}
                  <col style={{width:'3%'}}/>  {/* Cmp Sta */}
                  <col style={{width:'3%'}}/>  {/* Pre-Bid */}
                  <col style={{width:'2%'}}/>  {/* % */}
                  <col style={{width:'4%'}}/>  {/* Start Date */}
                  <col style={{width:'4%'}}/>  {/* Comp Date */}
                  <col style={{width:'6%'}}/>  {/* Notes */}
                </colgroup>
                <thead>
                  <tr>
                    <TH>Job#</TH>
                    <TH>Date Sold</TH>
                    <TH>Name</TH>
                    <TH>Phone</TH>
                    <TH>Address</TH>
                    <TH>City</TH>
                    <TH>Product</TH>
                    <TH>Sales Rep</TH>
                    <TH>Job Status</TH>
                    <TH>Rem. Date</TH>
                    <TH>COD</TH>
                    <TH>Sub</TH>
                    <TH right>Amt Sold</TH>
                    <TH right>Deposit</TH>
                    <TH>Sta</TH>
                    <TH right>Rem Coll</TH>
                    <TH>Sta</TH>
                    <TH right>Sta Coll</TH>
                    <TH>Sta</TH>
                    <TH right>Cmp Coll</TH>
                    <TH>Sta</TH>
                    <TH>Pre-Bid</TH>
                    <TH>%</TH>
                    <TH>Start</TH>
                    <TH>Comp</TH>
                    <TH>Notes</TH>
                  </tr>
                </thead>
                <tbody>
                  {printJobs.map((job, idx) => {
                    const service = getServiceFromJob(job);
                    const bg = getProductColor(service);
                    const prevService = idx > 0 ? getServiceFromJob(printJobs[idx - 1]) : null;
                    const isNewGroup = service !== prevService;
                    return (
                      <>
                        {isNewGroup && idx > 0 && (
                          <tr key={`sep-${idx}`}>
                            <td colSpan={26} style={{height:'4px',background:'#fff',borderBottom:'1.5px solid #1f314e',padding:0}}/>
                          </tr>
                        )}
                        <tr key={job.id} style={{background:bg,pageBreakInside:'avoid',WebkitPrintColorAdjust:'exact',printColorAdjust:'exact'}}>
                          <TD bold>{job.po_number}</TD>
                          <TD>{formatDateShort(job.contract_date)}</TD>
                          <TD>{job.customer_name}</TD>
                          <TD small>{job.phone}</TD>
                          <TD small>{job.address}</TD>
                          <TD>{job.city}</TD>
                          <TD>{service}</TD>
                          <TD small>{displayRep(job)}</TD>
                          <TD small>{job.prod_status || job.sales_status}</TD>
                          <TD>{formatDateShort(job.rem_date)}</TD>
                          <TD small>{job.primary_lender ? (job.primary_lender + (job.primary_lender_amt ? ' $' + Number(job.primary_lender_amt).toLocaleString() : '')) : ''}</TD>
                          <TD small>{job.con_roof || job.con_ec || job.con_win || job.con_gut}</TD>
                          <TD right>{job.change_order_amt || job.contract_amt ? formatCurrency(job.change_order_amt || job.contract_amt) : ''}</TD>
                          <TD right>{job.deposit_amt ? formatCurrency(job.deposit_amt) : ''}</TD>
                          <TD small>{job.deposit_status}</TD>
                          <TD right>{job.rem_amt ? formatCurrency(job.rem_amt) : ''}</TD>
                          <TD small>{job.rem_status}</TD>
                          <TD right>{job.sta_amt ? formatCurrency(job.sta_amt) : ''}</TD>
                          <TD small>{job.sta_status}</TD>
                          <TD right>{job.cmp_amt ? formatCurrency(job.cmp_amt) : ''}</TD>
                          <TD small>{job.cmp_status}</TD>
                          <TD small>{job.bid_sent}</TD>
                          <TD small>{job.bid_pct}</TD>
                          <TD>{formatDateShort(job.start_date)}</TD>
                          <TD>{formatDateShort(job.end_date)}</TD>
                          <TD small>{job.notes}</TD>
                        </tr>
                      </>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>
    </>
  );
}
